'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CommandEvent, CommandState } from '@/command/types';
import type { RoomAgentAction } from '@/command/agent-types';

export function useCommandRoom() {
  const [state, setState] = useState<CommandState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [online, setOnline] = useState(true);
  const [transport, setTransport] = useState<'stream' | 'poll'>('poll');
  const [streamEpoch, setStreamEpoch] = useState(0);
  const [invitation, setInvitation] = useState('');
  const currentState = useRef<CommandState | null>(null);
  const invitationCache = useRef<{ identity: string; value: string } | null>(null);
  const pending = useRef(false);
  const mounted = useRef(true);
  const controllers = useRef(new Map<AbortController, string>());
  const polling = useRef<symbol | null>(null);
  const sessionEpoch = useRef(0);
  const stream = useRef<EventSource | null>(null);

  const stopStream = useCallback(() => {
    stream.current?.close();
    stream.current = null;
    if (mounted.current) setTransport('poll');
  }, []);

  const accept = useCallback((next: CommandState) => {
    const previous = currentState.current;
    const sameIdentity = previous?.room.id === next.room.id && previous.self.id === next.self.id;
    if (sameIdentity && (previous.revision > next.revision ||
      (previous.revision === next.revision && Date.parse(previous.serverTime) > Date.parse(next.serverTime)))) return;
    currentState.current = next;
    setState(next);
    const identity = `${next.room.id}:${next.self.id}`;
    if (next.readOnly) {
      invitationCache.current = { identity, value: '' };
      setInvitation('');
      return;
    }
    if (invitationCache.current?.identity !== identity) {
      let value = '';
      try { value = sessionStorage.getItem(`atrium-invite:${identity}`) || ''; } catch { /* A different identity never inherits an in-memory invitation. */ }
      invitationCache.current = { identity, value };
    }
    setInvitation(invitationCache.current.value);
  }, []);

  const clearIdentity = useCallback(() => {
    stopStream();
    currentState.current = null;
    invitationCache.current = null;
    setState(null);
    setInvitation('');
  }, [stopStream]);

  const invalidateReads = useCallback(() => {
    sessionEpoch.current++;
    setStreamEpoch(sessionEpoch.current);
    stopStream();
    polling.current = null;
    controllers.current.forEach((method, controller) => { if (method === 'GET') controller.abort(); });
    return sessionEpoch.current;
  }, [stopStream]);

  const request = useCallback(async (path: string, method = 'GET', data?: unknown) => {
    const controller = new AbortController();
    controllers.current.set(controller, method);
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`/api/command/${path}`, {
        method, credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        ...(data !== undefined ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(data) } : {}),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(response.status === 401 ? 'session_required' : body.error || 'request_failed');
      return body;
    } finally {
      window.clearTimeout(timeout);
      controllers.current.delete(controller);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!mounted.current || pending.current || polling.current) return;
    const epoch = sessionEpoch.current;
    const poll = Symbol('command-poll');
    polling.current = poll;
    try {
      const next = await request('state') as CommandState;
      if (!mounted.current || epoch !== sessionEpoch.current || polling.current !== poll) return;
      accept(next); setOnline(true);
    } catch (cause) {
      if (!mounted.current || epoch !== sessionEpoch.current || polling.current !== poll) return;
      if ((cause as Error).message === 'session_required') { clearIdentity(); setOnline(true); }
      else setOnline(false);
    } finally {
      if (polling.current === poll) polling.current = null;
      if (mounted.current && epoch === sessionEpoch.current) setLoading(false);
    }
  }, [accept, clearIdentity, request]);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    const timer = window.setInterval(() => { if (!document.hidden && !pending.current && stream.current?.readyState !== 1) void refresh(); }, 5000);
    const resume = () => { if (!document.hidden) void refresh(); };
    document.addEventListener('visibilitychange', resume);
    const activeControllers = controllers.current;
    return () => {
      mounted.current = false;
      sessionEpoch.current++;
      polling.current = null;
      pending.current = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', resume);
      activeControllers.forEach((_, controller) => controller.abort());
    };
  }, [refresh]);

  const roomId = state?.room.id;
  const selfId = state?.self.id;
  useEffect(() => {
    if (!roomId || !selfId || busy || pending.current || typeof EventSource === 'undefined') return;
    const epoch = sessionEpoch.current;
    const source = new EventSource('/api/command/stream');
    stream.current = source;
    const active = () => mounted.current && stream.current === source && epoch === sessionEpoch.current &&
      !pending.current && currentState.current?.room.id === roomId && currentState.current?.self.id === selfId;

    source.onopen = () => { if (active()) { setTransport('stream'); setOnline(true); } };
    source.addEventListener('snapshot', (event: MessageEvent<string>) => {
      if (!active()) return;
      try {
        const next = JSON.parse(event.data) as CommandState;
        if (next?.room?.id !== roomId || next?.self?.id !== selfId ||
          !Number.isSafeInteger(next.revision) || !Number.isFinite(Date.parse(next.serverTime)) ||
          !Array.isArray(next.members) || !Array.isArray(next.tasks) || !Array.isArray(next.messages)) return;
        accept(next);
        setTransport('stream');
        setOnline(true);
      } catch { /* The authenticated GET remains available if a frame cannot be parsed. */ }
    });
    source.addEventListener('session-expired', () => {
      if (!active()) return;
      invalidateReads();
      clearIdentity();
      setLoading(false);
      setOnline(true);
    });
    // EventSource reconnects after the server's bounded stream renewal. GET
    // polling also detects a revoked session when reconnect itself returns 401.
    source.onerror = () => {
      if (!active()) return;
      setTransport('poll');
      void refresh();
    };
    return () => {
      source.close();
      if (stream.current === source) stopStream();
    };
  }, [roomId, selfId, busy, streamEpoch, accept, clearIdentity, invalidateReads, refresh, stopStream]);

  const mutate = useCallback(async (path: string, data: unknown, method = 'POST') => {
    if (!mounted.current || pending.current) return null;
    if (currentState.current?.readOnly) { setError('agent_read_only'); return null; }
    pending.current = true; setBusy(true); setError(null);
    // A write supersedes any GET issued before it. Identity-changing writes
    // also retire the previous invitation before a new guest can become active.
    const epoch = invalidateReads();
    const changesIdentity = path === 'rooms' || path === 'join' || path === 'session';
    if (changesIdentity) { invitationCache.current = null; setInvitation(''); }
    let recover = false;
    try {
      const result = await request(path, method, data);
      if (!mounted.current || epoch !== sessionEpoch.current) return null;
      if (result.invitation && result.state) {
        const identity = `${result.state.room.id}:${result.state.self.id}`;
        invitationCache.current = { identity, value: result.invitation };
        try { sessionStorage.setItem(`atrium-invite:${identity}`, result.invitation); } catch { /* The current identity retains its invitation in memory. */ }
      }
      if (result.state) accept(result.state);
      else if (path === 'session') clearIdentity();
      setOnline(true);
      return result as { state: CommandState; invitation?: string };
    } catch (cause) {
      if (mounted.current && epoch === sessionEpoch.current) {
        setError((cause as Error).message);
        if ((cause as Error).message === 'session_required') clearIdentity();
        else recover = true;
      }
      return null;
    } finally {
      if (mounted.current && epoch === sessionEpoch.current) {
        pending.current = false;
        setBusy(false);
        setLoading(false);
        if (recover) void refresh();
      }
    }
  }, [accept, clearIdentity, invalidateReads, refresh, request]);

  const send = useCallback((event: CommandEvent) => mutate('events', event), [mutate]);
  const agentAction = useCallback((action: RoomAgentAction) => mutate('agents', action), [mutate]);

  useEffect(() => {
    if (!state?.room.id || state.readOnly) return;
    const heartbeat = () => { if (!document.hidden && !pending.current) void send({ type: 'heartbeat' }); };
    const timer = window.setInterval(heartbeat, 20000);
    return () => window.clearInterval(timer);
  }, [state?.room.id, state?.self.id, state?.readOnly, send]);

  const leave = useCallback(async () => {
    await mutate('session', undefined, 'DELETE');
  }, [mutate]);

  return { state, loading, busy, error, online, transport, invitation, refresh, send, agentAction, leave,
    create: (name: string, goal: string) => mutate('rooms', { name, goal }),
    join: (name: string, invitation: string) => mutate('join', { name, invitation }),
    clearError: () => setError(null),
  };
}
